import { useEffect, useRef } from "react";

import {
  TaxonomyApprovalPipelineJobDocument,
  type TaxonomyApprovalPipelineJobQuery,
  type TaxonomyApprovalPipelineSubscription,
} from "@/graphql/generated";
import { fetcher } from "@/graphql/fetcher";
import { isApprovalPipelineTerminal } from "@/lib/attributeReview/approvalPipeline";

type PipelineUpdate =
  TaxonomyApprovalPipelineSubscription["taxonomyApprovalPipeline"];

export interface UseApprovalPipelineJobFallbackPollOptions {
  syncJobIds: Array<string | null | undefined>;
  enabled?: boolean;
  intervalMs?: number;
  onTerminal?: (update: PipelineUpdate) => void;
}

/**
 * Safety net for a terminal event that never arrives.
 *
 * The chip and the row's Approved/Needs-Review placement are driven purely by
 * the pipeline subscription. Every hop it depends on — the engine's Redis
 * publish, the worker consuming that stream, the API's Redis subscriber — can
 * drop a message, and when one does the row stays pinned behind "Approving…"
 * forever even though the job finished. Polling the job itself is the one
 * signal that cannot go missing, so it backstops the subscription rather than
 * replacing it: whichever reports terminal first wins, and the caller's
 * existing de-duplication ignores the loser.
 */
export function useApprovalPipelineJobFallbackPoll({
  syncJobIds,
  enabled = true,
  intervalMs = 5000,
  onTerminal,
}: UseApprovalPipelineJobFallbackPollOptions): void {
  const onTerminalRef = useRef(onTerminal);
  onTerminalRef.current = onTerminal;
  // Ids already observed terminal. The caller de-dupes its handling, but
  // re-fetching a finished job every tick is pure waste against a shared
  // database, and the polls only stop when the caller happens to drop the id.
  const settledIdsRef = useRef(new Set<string>());
  // One warning per id, not one per tick — a broken poll would otherwise
  // bury the console at the interval rate.
  const warnedIdsRef = useRef(new Set<string>());

  const idsKey = Array.from(
    new Set(
      syncJobIds.filter(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0,
      ),
    ),
  )
    .sort()
    .join(",");

  useEffect(() => {
    if (!enabled || !idsKey) return;
    const ids = idsKey.split(",");
    let cancelled = false;

    // Both Sets are keyed by job id and would otherwise grow for the life of
    // the tab — a governance session runs for hours and approves constantly.
    const active = new Set(ids);
    for (const settled of settledIdsRef.current) {
      if (!active.has(settled)) settledIdsRef.current.delete(settled);
    }
    for (const warned of warnedIdsRef.current) {
      if (!active.has(warned)) warnedIdsRef.current.delete(warned);
    }

    const poll = async () => {
      for (const syncJobId of ids) {
        if (cancelled) return;
        if (settledIdsRef.current.has(syncJobId)) continue;
        try {
          const data = await fetcher<
            TaxonomyApprovalPipelineJobQuery,
            { syncJobId: string }
          >(TaxonomyApprovalPipelineJobDocument, { syncJobId })();
          const job = data?.taxonomyApprovalPipelineJob?.job;
          if (!job || cancelled) continue;
          if (!isApprovalPipelineTerminal(String(job.status))) continue;
          settledIdsRef.current.add(syncJobId);
          onTerminalRef.current?.({
            // Synthesised: the poll observed the job's state rather than
            // receiving an event, so there is no real event to name.
            eventId: `fallback-poll:${job.syncJobId}:${job.status}`,
            eventType: job.lastLedgerEventType ?? "FALLBACK_POLL",
            syncJobId: job.syncJobId,
            taxonomyJobId: job.taxonomyJobId ?? null,
            pipelineId: job.pipelineId ?? null,
            mappingSessionId: job.mappingSessionId,
            resourceLockKey: job.resourceLockKey,
            status: job.status,
            taxonomyStatus: job.taxonomyStatus ?? null,
            retryable: Boolean(job.retryable),
            message: job.message ?? null,
            error: job.error ?? null,
            errorType: job.errorType ?? null,
            errorDetails: null,
            affected: null,
            payload: job.payload ?? null,
            terminal: true,
            occurredAt: job.completedAt ?? job.updatedAt ?? job.createdAt,
            fieldResults: [],
            valueResults: [],
          });
        } catch (error) {
          // A failed poll is not a failed job — the next tick retries. But a
          // safety net that is itself silently broken is the exact failure
          // this hook exists to catch, so say so once per id rather than
          // never, and never once per tick.
          if (!warnedIdsRef.current.has(syncJobId)) {
            warnedIdsRef.current.add(syncJobId);
            console.warn(
              "[approval-pipeline] fallback poll failed; the Approving chip may not clear on its own",
              { syncJobId, error },
            );
          }
        }
      }
    };

    // Self-scheduling rather than setInterval: a poll slower than the interval
    // would otherwise stack requests on top of each other, and it stops once
    // every id has been seen terminal.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      // Anything escaping poll() would otherwise reject this tick, skip the
      // reschedule, and kill the safety net for the rest of the session --
      // silently, which is the failure this hook exists to prevent.
      try {
        await poll();
      } catch {
        // Already reported per id inside poll(); keep the loop alive.
      }
      if (cancelled) return;
      if (ids.every((syncJobId) => settledIdsRef.current.has(syncJobId)))
        return;
      timer = setTimeout(() => void tick(), intervalMs);
    };
    timer = setTimeout(() => void tick(), intervalMs);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, idsKey, intervalMs]);
}
